<template>
  <BgImageLayout title="User Settings" :page="__guide.userSettings" icon-color="white">
    <ConfirmationDialog v-if="deletable" :uid="uid" :entity="deletable" :replacement-options="[]">
      <template #custom-title="{data}"> Confirm {{ data.name ? `deactivation of ${data.name}` : '' }} </template>

      <template #replaceable-options>
        <q-select
          v-model="userToReplaceId"
          :options="users"
          label="Select user"
          map-options
          emit-value
          option-value="id"
          option-label="name"
          outlined
        />
      </template>

      <template #replaceable-actions>
        <div class="col-5 modal-actions q-mb-md">
          <q-btn
            :disable="userToReplaceId === deletable.id"
            label="Confirm"
            color="negative"
            @click="deactivateUser(deletable.id, userToReplaceId)"
          >
            <LmTooltip> Deactivate and transfer ownership </LmTooltip>
          </q-btn>

          <q-btn label="Cancel" flat @click="deletable = null" />
        </div>
      </template>

      <template #non-replaceable-actions>
        <div class="col-auto q-mr-md ">
          <q-btn label="Confirm" color="negative" @click="deactivateUser(deletable.id)" />
        </div>

        <div class="col-auto q-mr-md ">
          <q-btn label="Cancel" flat @click="deletable = null" />
        </div>
      </template>
    </ConfirmationDialog>

    <UserModal ref="createUserModal" @user-added="addUser" />

    <UserModal
      ref="editUserModal"
      :is-last-active-admin="isLastActiveAdmin"
      modal-type="edit"
      modal-title="Edit User"
      @user-updated="updateUser"
    />

    <PasswordModal ref="passwordModal" />

    <GAEdialog v-model="GAEdialog" :user="user" :gae-required="false" @updated="updateUser" />

    <q-table
      :data="orderedUsers"
      :columns="columns"
      :pagination.sync="pagination"
      :loading="tableLoading"
      loading-label="Fetching users"
      row-key="name"
      class="table"
    >
      <template v-if="canEdit" #top-right>
        <CreateUserButton @click="openCreateUserModal" />
      </template>

      <q-tr
        slot="body"
        slot-scope="props"
        :props="props"
        :class="{ 'current-user-row': props.row.current }"
        class="cursor-pointer"
      >
        <q-td v-for="col in props.cols" :key="col.name" :props="props" :class="{ 'text-faded': !props.row.active }">
          <template v-if="col.name === 'action'">
            <TableAction :show="props.row.current || canEdit" @click="editRow(props.row)" />

            <TableAction
              :show="props.row.current || canEdit"
              icon-class="fas fa-key"
              :tooltip="props.row.current || !props.row.is_system_user ? 'Change Password' : 'Reset Password'"
              @click="
                props.row.current || !props.row.is_system_user
                  ? openPasswordModal(props.row)
                  : confirmResetPassword(props.row)
              "
            />

            <TableAction
              :show="props.row.showGAEicon"
              icon-class="fab fa-google"
              tooltip="Set google account email"
              @click="openGAEdialog(props.row)"
            />

            <q-toggle
              :show="!props.row.current"
              :value="props.row.active"
              color="tertiary"
              dense
              @input="toggleUserActivation(props.row)"
            >
              <LmTooltip>
                This user is {{ !props.row.active ? 'not' : '' }} active. Toggle to
                {{ props.row.active ? 'Deactivate' : 'Activate' }}
              </LmTooltip>
            </q-toggle>
          </template>
          <span v-else-if="col.name === 'active'">
            {{ col.value ? 'Yes' : 'No' }}
          </span>
          <span v-else-if="!col.value">N/A</span>
          <span v-else-if="col.name === 'role'">{{ col.value.name | capitalize }}</span>
          <span v-else>{{ col.value }}</span>
        </q-td>
      </q-tr>
    </q-table>
  </BgImageLayout>
</template>

<script>
import * as apiTo from '@/modular-api'
import { generateDialogParams } from '@/lib/generic.utils'
// Libraries
import _orderBy from 'lodash/orderBy'
import { uid } from 'quasar'
// Components
import GAEdialog from '@/reusable/GAEdialog'
import ConfirmationDialog from '@components/lm-blocks/Dialog'
import UserModal from './components/user-modal/UserModal'
import PasswordModal from './components/PasswordModal'
import CreateUserButton from './components/CreateUserButton'
import TableAction from './components/TableAction'
import ConfirmationDialog2 from '@/reusable/ConfirmationDialog/ConfirmationDialog'

export default {
  components: {
    GAEdialog,
    ConfirmationDialog,
    UserModal,
    PasswordModal,
    CreateUserButton,
    TableAction
  },

  data: () => ({
    pagination: {
      rowsPerPage: 30
    },
    users: [],
    tableLoading: false,
    deletable: null,
    uid: uid(),
    userToReplaceId: null,
    isRequestInProgress: false,
    GAEdialog: false,
    user: null
  }),

  computed: {
    loggedUser() {
      return this.$store.state.user
    },

    orderedUsers() {
      return _orderBy(this.users, 'active', 'desc')
    },

    canEdit() {
      if (!this.$store.state.user) return
      const permissions = this.$store.state.user.permissions
      return permissions.some(p => p.name === 'edit-users')
    },

    canPDI() {
      if (!this.$store.state.user) return
      const permissions = this.$store.state.user.permissions
      return permissions.some(p => p.name === 'perform-data-imports')
    },

    isLastActiveAdmin() {
      const activeAdmins = this.users.filter(user => {
        return user.role.name === 'admin' && user.active
      })
      return activeAdmins.length === 1
    }
  },

  created() {
    this.columns = [
      {
        name: 'name',
        required: true,
        label: 'Name',
        align: 'left',
        field: 'name',
        sortable: true
      },
      {
        name: 'email',
        label: 'Email',
        align: 'left',
        field: 'email',
        sortable: true
      },
      {
        name: 'phone',
        label: 'Phone',
        align: 'left',
        field: 'phone',
        sortable: true
      },
      {
        name: 'job',
        label: 'Job',
        align: 'left',
        field: 'job',
        sortable: true
      },
      {
        name: 'role',
        label: 'Role',
        align: 'left',
        field: 'role',
        sortable: true
      },
      {
        name: 'active',
        label: 'Active',
        align: 'left',
        field: 'active',
        sortable: true
      },
      {
        align: 'center',
        name: 'action',
        label: 'Actions'
      }
    ]
    this.getUsers()
  },

  methods: {
    getUsers() {
      this.tableLoading = true
      apiTo
        .GET_USERS()
        .then(({ data }) => this.setUsers(data.result))
        .finally(() => (this.tableLoading = false))
    },

    setUsers(users) {
      this.users = users.map(user => ({ ...user, showGAEicon: this.shouldShowGAEicon(user) }))
    },

    shouldShowGAEicon(user) {
      const hasPDI = user.permissions.some(p => p.name === 'perform-data-imports')
      const canPDItoOthers = this.canPDI && this.canEdit && hasPDI
      return canPDItoOthers || (user.current && this.canPDI)
    },

    openGAEdialog(user) {
      this.user = user
      this.GAEdialog = true
    },

    openCreateUserModal() {
      this.$refs.createUserModal.openModal()
    },

    addUser(newUser) {
      this.users.unshift({ ...newUser, showGAEicon: this.shouldShowGAEicon(newUser) })
    },

    updateUser(updatedUser) {
      const index = this.users.findIndex(user => user.id === updatedUser.id)
      if (~index) {
        this.users.splice(index, 1, { ...updatedUser, showGAEicon: this.shouldShowGAEicon(updatedUser) })
      }
      if (this.loggedUser.id === updatedUser.id) {
        this.$store.commit('updateUser', updatedUser)
        this.$q.localStorage.set(`${this.$store.getters.tenant}_user`, updatedUser)
      }
    },

    editRow(row) {
      this.$refs.editUserModal.openModal(row)
    },

    openPasswordModal(row) {
      this.$refs.passwordModal.openModal(row.id)
    },

    toggleUserActivation(user) {
      if (this.isRequestInProgress) return
      if (!user.active) {
        this.isRequestInProgress = true
        apiTo
          .ACTIVATE_USER(user.id)
          .then(({ data }) => this.updateUser(data.result))
          .finally(() => (this.isRequestInProgress = false))
      } else {
        this.deletable = user
        this.userToReplaceId = user.id
        this.isRequestInProgress = true
        apiTo
          .GET_USER_DEACTIVATE_CONFIRMATION(user.id)
          .then(response => {
            this.$store.commit(
              'updateGlobalDialogContent',
              generateDialogParams(this.uid, 'deleteSingle', response.data)
            )
            this.$store.commit('updateGlobalDialogVisibility', {
              uid: this.uid,
              value: true
            })
          })
          .finally(() => (this.isRequestInProgress = false))
      }
    },

    deactivateUser(id, replaceId) {
      if (this.isRequestInProgress) return
      this.isRequestInProgress = true
      apiTo
        .DEACTIVATE_USER(id, replaceId)
        .then(({ data }) => {
          this.$store.dispatch('updateGlobalDialogVisibility', {
            uid: this.uid,
            value: false
          })
          this.updateUser(data.result)
          this.deletable = null
          this.userToReplaceId = null
        })
        .finally(() => (this.isRequestInProgress = false))
    },

    confirmResetPassword(user) {
      this.$q
        .dialog({
          component: ConfirmationDialog2,
          title: `Are you sure you want to reset this user's password?`
        })
        .onOk(() => {
          this.resetPassword(user)
        })
    },

    resetPassword(user) {
      if (this.isRequestInProgress) return
      this.isRequestInProgress = true
      apiTo
        .RESET_PASSWORD(user.id)
        .then(({ data }) => this.updateUser(data.result))
        .finally(() => (this.isRequestInProgress = false))
    }
  }
}
</script>

<style lang="stylus" scoped>
.table {
  background: white
  td {
    height 48px
  }
  .current-user-row {
    background-color: rgba(68, 178, 195, 0.2)
  }
}

.modal-actions {
  display grid
  grid-auto-flow column
  gap 10px

}
</style>
